import { useCallback } from 'react';
import { Link } from 'react-router-dom';

import { Pagination } from '../../../components/pagination/Pagination';
import { ITableHeader, Table, TableRow } from '../../../components/table/Table';
import { formatDateTime } from '../../../utils/date';
import { IPaginationVariables, usePagination } from '../../../hooks/usePagination';
import { EmptyState } from '../../../components/empty-state/EmptyState';
import { AuditLogsFilter, getAuditLogs, type AuditLog as AuditLogModel } from '../models';

export type AuditLog = AuditLogModel;

export const AUDIT_LOG_TABLE_HEADERS: ITableHeader[] = [
  {
    id: 'description',
    name: 'Beschrijving',
  },
  {
    id: 'actionType',
    name: 'Actie',
  },
  {
    id: 'actorName',
    name: 'Auteur',
  },
  {
    id: 'createdAt',
    name: 'Aangemaakt op',
  },
];

/** Row renderer shared by the embedded list below and the standalone audit logs page. */
export function mapAuditLogRow(auditLog: AuditLog): TableRow {
  return [
    <Link to={`/internal/audit-logs/${auditLog.id}`} className="link-text">
      {auditLog.description}
    </Link>,
    auditLog.actionType,
    auditLog.actorName,
    formatDateTime(auditLog.createdAt),
  ];
}

export interface IAuditLogsListProps {
  filter: AuditLogsFilter;
  /** Change it to reload the list, e.g. after an action that writes audit logs. */
  refreshToken?: unknown;
}

export const AuditLogsList: React.FC<IAuditLogsListProps> = (props) => {
  const { filter, refreshToken } = props;
  const pageFetcher = useCallback(
    async (variables: IPaginationVariables) => {
      return getAuditLogs(variables.cursor, variables.take, filter);
    },
    // Matches the original behavior: the fetcher identity is stable, so the
    // filter is captured at mount (each list instance has a fixed filter) and
    // changing it does not trigger a refetch loop via usePagination.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const page = usePagination({
    key: 'id',
    pageSize: 20,
    initialCursor: undefined,
    fetcher: pageFetcher,
    refreshToken,
  });

  return (
    <div>
      <div className="mb-8">
        {page.data.length || page.error || page.isFetching ? (
          <Table
            idKey="id"
            headers={AUDIT_LOG_TABLE_HEADERS}
            data={page.data}
            error={page.error}
            onRetry={page.retry}
            isLoading={page.isFetching}
            mapData={mapAuditLogRow}
          />
        ) : (
          <EmptyState title="Geen audit logs gevonden" />
        )}
      </div>

      <div className="my-4">
        <Pagination
          hasPrevious={page.hasPrevious}
          previous={page.previous}
          hasNext={page.hasNext}
          next={page.next}
          isFetching={page.isFetching}
        />
      </div>
    </div>
  );
};
